import { useEffect, useRef } from 'react';
import { useCaptureCameFrom } from '../app/came-from';
import { SessionRestoreGate, restoringSettings } from '../app/session-restore';
import { SettingsPlaceholder, isSettingsPath } from '../app/settings-placeholder';
import { Outlet, useLocation } from 'react-router';
import {
  AUTH_CALLBACK_PATH,
  LoadingState,
  LoginShell,
  PRODUCT_TAGLINES,
  ProductLockup,
  SigningIn,
  StatusView,
  signingInTitle,
  traceButtonClass,
} from '@traceio-org/auth-ui';
import { PRODUCTS, isProductKey, normalizeOrigin, type ProductKey } from '@traceio-org/platform-contracts';
import { signingInProduct } from '../app/signing-in-product';
import { safeHubPath } from '../security/hub-path';
import { useBootstrap } from '../app/context';
import { HUB_TAGLINE } from '../app/ui';
import { NotSetUpPage } from './StatusPages';

/** Root route: an unknown host replaces every page with the not-set-up page (no sign-in form). */
export function RootLayout() {
  const { host } = useBootstrap();
  // ?from=<product> on any page: remembered for the tab and taken out of the address (app/came-from.ts).
  useCaptureCameFrom();
  if (host.kind === 'unknown') return <NotSetUpPage reason={host.reason} />;
  // A reload keeps the person signed in: settings and invitation pages restore the session silently first.
  return (
    <SessionRestoreGate>
      <Outlet />
    </SessionRestoreGate>
  );
}

/**
 * True on the hub's own origin (VITE_HUB_ORIGIN), where the page is Trace's whatever the host lookup answers. A
 * customer domain is any other origin: there nothing Trace-branded may show before the host is known.
 */
function onOwnOrigin(): boolean {
  const configured = import.meta.env.VITE_HUB_ORIGIN?.trim();
  if (!configured) return false;
  try {
    return normalizeOrigin(configured) === window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Shown while the root loader resolves the host. On a customer domain it is neutral on purpose: before the host
 * is known it cannot tell Trace branding from customer branding. On the hub's own origin it already carries the
 * artwork HubFrame shows next (and the static shell of index.html showed first, @traceio-org/auth-ui/vite-shell),
 * so the logo never disappears and comes back while the page boots.
 */
export function BootLoading() {
  const { pathname } = useLocation();
  const signingIn = bootSigningInProduct(pathname);
  const own = onOwnOrigin();
  const product = signingIn ?? null;
  const brand = own ? (
    product ? (
      <ProductLockup productKey={product} surface="dark" />
    ) : (
      <ProductLockup lockup="trace" surface="dark" />
    )
  ) : null;
  const tagline = own ? (product ? PRODUCT_TAGLINES[product] : HUB_TAGLINE) : undefined;
  // A settings page, or the callback of its silent restore: the settings placeholder the static first paint already
  // shows (vite/settings-placeholder-shell.ts), so booting changes nothing on screen.
  if (isSettingsPath(pathname) || (pathname.startsWith(AUTH_CALLBACK_PATH) && restoringSettings())) {
    return <SettingsPlaceholder brand={own} />;
  }
  if (signingIn !== undefined) {
    // The first frame of a sign-in page or the callback: the same heading as the page that follows,
    // so the host lookup does not add a screen of its own.
    const name = signingIn ? PRODUCTS[signingIn].displayName : 'Trace';
    return (
      <LoginShell brand={brand} tagline={tagline}>
        <SigningIn title={signingInTitle(name)} />
      </LoginShell>
    );
  }
  return (
    <LoginShell brand={brand} tagline={tagline}>
      <h1 className="trace-auth-visually-hidden">Loading</h1>
      <LoadingState label="Loading" />
    </LoginShell>
  );
}

/**
 * The product a boot screen on this path signs in to: from /login/<product>, or remembered for the callback;
 * null on /login, a callback with nothing remembered and the pages a reload restores (settings, invitations);
 * undefined on any other page (a plain loading screen).
 */
function bootSigningInProduct(pathname: string): ProductKey | null | undefined {
  if (pathname.startsWith(AUTH_CALLBACK_PATH)) return signingInProduct();
  // A page a reload restores silently (settings, invitations: app/session-restore.tsx) boots on the same signing-in
  // screen the restore shows next, so a reload is one steady screen instead of "Loading" then "Signing you in".
  if (safeHubPath(pathname) !== undefined) return null;
  const match = /^\/login(?:\/([a-z]+))?\/?$/.exec(pathname);
  if (!match) return undefined;
  return isProductKey(match[1]) ? match[1] : null;
}

function useStaticHeading(title: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${title} | Trace`;
    ref.current?.focus({ preventScroll: true });
  }, [title]);
  return ref;
}

/** Last-resort error boundary of the router. */
export function RootError() {
  const title = 'Something went wrong';
  const headingRef = useStaticHeading(title);
  return (
    <LoginShell brand={<ProductLockup lockup="trace" surface="dark" />} tagline={HUB_TAGLINE}>
      <StatusView
        tone="error"
        title={title}
        headingRef={headingRef}
        primaryAction={{ label: 'Reload the page', onClick: () => window.location.reload() }}
      >
        <p>The Trace account hub hit an unexpected error. Reloading usually fixes it.</p>
      </StatusView>
    </LoginShell>
  );
}

/** Rendered instead of the app when the build environment is invalid (see config.ts). */
export function ConfigErrorPage({ errors }: { errors: string[] }) {
  const title = 'The account hub is not configured';
  const headingRef = useStaticHeading(title);
  return (
    <LoginShell brand={<ProductLockup lockup="trace" surface="dark" />} tagline={HUB_TAGLINE}>
      <StatusView tone="error" title={title} headingRef={headingRef}>
        <p>Fix these build settings, then rebuild:</p>
        <ul className="trace-auth-config-errors">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </StatusView>
    </LoginShell>
  );
}

/** Rendered instead of the app when it is loaded inside a frame (see app/framing.ts). */
export function FramedPage() {
  const title = 'Open Trace sign-in in its own window';
  const headingRef = useStaticHeading(title);
  return (
    <LoginShell brand={<ProductLockup lockup="trace" surface="dark" />} tagline={HUB_TAGLINE}>
      <StatusView
        tone="warning"
        title={title}
        headingRef={headingRef}
        primaryAction={
          <a
            className={traceButtonClass('primary', { block: true })}
            href={window.location.href}
            target="_blank"
            rel="noopener noreferrer"
          >
            Open Trace sign-in
          </a>
        }
      >
        <p>For your security, Trace sign-in does not run inside another page.</p>
      </StatusView>
    </LoginShell>
  );
}
